'use client'

import { BecomeMemberBlock } from '@/blocks/BecomeMember/Component'
import CardCarousel from '@/components/common/CardCarousel'
import { transformToListCards } from '@/lib/helpers/transformToListCard'
import { TrustYouResponse } from '@/lib/helpers/transformTrustYou'
import { City, Hotel } from '@/payload-types'
import { useEffect, useMemo, type ReactNode } from 'react'
import { useTranslations } from 'next-intl'
import { useBookingBarStore } from '@/lib/context/bookingBarContext'

export type HotelWithTrustYou = Hotel & {
  trustYou?: TrustYouResponse
}

type ExtendedCity = Omit<City, 'hotels'> & {
  hotels: {
    docs: HotelWithTrustYou[]
    hasNextPage?: boolean
    totalDocs?: number
  }
}

const CityClient = ({
  city,
  hotelsSlot,
}: {
  city: ExtendedCity
  /** Server-rendered grid: it reads availability, which the client can't. */
  hotelsSlot: ReactNode
}) => {
  // const [openAmenityDialog, setOpenAmenityDialog] = useState(false)
  // const [selectedAmenity, setSelectedAmenity] = useState<AmenityContent>(null)

  const t = useTranslations()

  const { setCitySlug, setCountrySlug } = useBookingBarStore()

  // Point the booking bar at this city, the store is persisted and may hold a previous search
  useEffect(() => {
    setCitySlug(city.slug || null)
    setCountrySlug(typeof city.country === 'object' ? city.country.slug || null : null)
  }, [city.slug, city.country, setCitySlug, setCountrySlug])

  const thingsToDo = useMemo(() => {
    if (typeof city === 'number' || !city['things-to-do']) return []

    return transformToListCards(city['things-to-do'].docs, `/things-to-do`)
  }, [city])

  const offers = useMemo(() => {
    if (typeof city === 'number') return []

    const hotelOffers = city.hotels?.docs
      ?.flatMap((hotel) => {
        if (typeof hotel !== 'number') {
          return hotel.offers?.docs?.map((offer) => {
            if (typeof offer !== 'number') {
              return offer
            }
          })
        }
      })
      .filter((offer) => offer !== undefined)

    return transformToListCards(hotelOffers, `/offers/hotel`)
  }, [city])

  return (
    <div className='space-y-12'>
      <div className='space-y-4'>
        <h3>{city.hotels?.docs.length > 1 ? t('hotels') : t('hotel')}</h3>

        {hotelsSlot}
      </div>

      {thingsToDo && thingsToDo.length > 0 && (
        <CardCarousel title={`${t('things_to_do_in')} ${city && city.title}`} items={thingsToDo} />
      )}

      {offers && offers.length > 0 && (
        <CardCarousel
          title={`${t('our_best_offers_in')} ${city && city.title}`}
          items={offers}
          link={`/offers`}
          linkTitle={t('view_all_offers')}
        />
      )}

      {/* BECOME A MEMBER */}
      <BecomeMemberBlock className='mx-0' />
    </div>
  )
}

export default CityClient
